import React from 'react';
import { Activity, Calendar, Clock, MapPin, Hourglass, UtensilsCrossed, TreePine } from 'lucide-react';
import { Tour } from '../../types/tours';
import { formatTime } from '../../utils/dateUtils';

interface TourDetailInfoProps {
  tour: Tour;
}

const TourDetailInfo: React.FC<TourDetailInfoProps> = ({ tour }) => {
  const lowestPrice = Math.min(
    tour.group_tour_price,
    tour.reduced_group_price,
    tour.private_price
  );

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-barrio text-4xl md:text-5xl text-[#2A382A] mb-4">
          {tour.name}
        </h1>
        <p className="text-2xl text-[#953B0E] font-semibold">
          From ${Math.min(
            tour.group_tour_price,
            tour.reduced_group_price || Number.MAX_VALUE,
            tour.private_price || Number.MAX_VALUE
          )}
        </p>
      </div>

      <div className="h-px bg-[#2A382A]/10" />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#2A382A] rounded-lg">
            <Activity className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-[#2A382A]/60">Activity Level</p>
            <p className="font-medium text-[#2A382A] capitalize">{tour.activity_level}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#2A382A] rounded-lg">
            <Calendar className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-[#2A382A]/60">Availability</p>
            <p className="font-medium text-[#2A382A]">{tour.availability}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#2A382A] rounded-lg">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-[#2A382A]/60">Departure</p>
            <div>
              <p className="font-medium text-[#2A382A]">
                {formatTime(tour.departure).americanTime}
              </p>
              <p className="text-xs text-[#2A382A]/60">
                {formatTime(tour.departure).europeanTime}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#2A382A] rounded-lg">
            <Hourglass className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-[#2A382A]/60">Duration</p>
            <p className="font-medium text-[#2A382A]">{tour.duration}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#2A382A] rounded-lg">
            <TreePine className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-[#2A382A]/60">Tour Location</p>
            <p className="font-medium text-[#2A382A]">{tour.location}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-3 bg-[#2A382A] rounded-lg">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-[#2A382A]/60">Meeting Point</p>
            <a 
              href={tour.meeting_point_url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-[#2A382A] hover:text-[#953B0E] transition-colors underline"
            >
              {tour.meeting_point}
            </a>
          </div>
        </div>
      </div>

      <div className="h-px bg-[#2A382A]/10 my-8" />

      <div className="space-y-4">
        <h2 className="text-xl font-bold text-[#2A382A]">
          {tour.tour_detail_headline}
        </h2>
        <p className="text-[#2A382A]/80 whitespace-pre-line leading-relaxed">
          {tour.tour_detail_description}
        </p>
      </div>
    </div>
  );
};

export default TourDetailInfo;